import { useEffect } from "react"
import { IconAlertCircle, IconCircleCheck, IconX } from "@tabler/icons-react"

type Variant = "success" | "error"

type Props = {
  variant: Variant
  message: string
  onDismiss: () => void
  autoDismissMs?: number
}

const variantStyles: Record<
  Variant,
  { container: string; icon: string; dismiss: string; accent: string }
> = {
  success: {
    container: "border-emerald-200/90 bg-emerald-50/95 text-emerald-950",
    icon: "text-emerald-600",
    dismiss: "text-emerald-700/70 hover:bg-emerald-100 hover:text-emerald-900",
    accent: "bg-emerald-500",
  },
  error: {
    container: "border-red-200/90 bg-red-50/95 text-red-950",
    icon: "text-red-600",
    dismiss: "text-red-700/70 hover:bg-red-100 hover:text-red-900",
    accent: "bg-red-500",
  },
}

const AdminAlert = ({ variant, message, onDismiss, autoDismissMs }: Props) => {
  useEffect(() => {
    if (autoDismissMs == null) return
    const timer = window.setTimeout(onDismiss, autoDismissMs)
    return () => window.clearTimeout(timer)
  }, [message, autoDismissMs, onDismiss])

  const styles = variantStyles[variant]
  const Icon = variant === "success" ? IconCircleCheck : IconAlertCircle

  return (
    <div
      role="alert"
      className={`relative overflow-hidden rounded-xl border shadow-[0_8px_24px_-12px_rgba(0,32,92,0.18)] ${styles.container}`}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${styles.accent}`} aria-hidden />

      <div className="flex items-start gap-3 py-3.5 pl-5 pr-3">
        <Icon size={20} stroke={1.75} className={`mt-0.5 shrink-0 ${styles.icon}`} aria-hidden />

        <p className="flex-1 pt-0.5 font-dm-sans text-sm leading-6">{message}</p>

        <button
          type="button"
          onClick={onDismiss}
          className={`shrink-0 rounded-lg p-1.5 transition ${styles.dismiss}`}
          aria-label="Dismiss notification"
        >
          <IconX size={16} stroke={2} />
        </button>
      </div>
    </div>
  )
}

export default AdminAlert
